"use client";

import { CONVERT_FEATURE_OPTIONS, type ConvertFeature } from "@fluentry/domain";
import { Tabs, TabsList, TabsTrigger } from "@fluentry/ui";

type FeatureControlsProps = {
  value: ConvertFeature;
  disabled?: boolean;
  onChange: (value: ConvertFeature) => void;
};

const activeDescription = (value: ConvertFeature) =>
  CONVERT_FEATURE_OPTIONS.find((option) => option.value === value)?.description ?? "";

const compactDescription = (value: ConvertFeature) => {
  switch (value) {
    case "interlingua":
      return "Resolve mixed-language drafts.";
    case "phrasing":
      return "Tighten tone and wording.";
    case "phonology":
      return "Recover likely words from sounds.";
    default:
      return activeDescription(value);
  }
};

export function FeatureControls({ value, disabled = false, onChange }: FeatureControlsProps) {
  return (
    <div className="grid gap-2 rounded-[1.1rem] border border-[#0f766e]/8 bg-white/62 p-3">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <span className="text-[10px] font-semibold tracking-[0.22em] text-[#5f7c80] uppercase">
          Assist mode
        </span>
        <span className="text-[11px] leading-5 text-[#648084]">{compactDescription(value)}</span>
      </div>
      {/* The three inference-bridge modes are one family, so they read as a segmented switch rather than separate tools. */}
      <Tabs
        value={value}
        onValueChange={(next) => onChange(next as ConvertFeature)}
        className="gap-0"
      >
        <TabsList className="grid h-auto w-full grid-cols-1 gap-1 rounded-[0.95rem] border border-[#0f766e]/8 bg-[#f7faf9] p-1 sm:grid-cols-3">
          {CONVERT_FEATURE_OPTIONS.map((option) => {
            const isActive = option.value === value;
            return (
              <TabsTrigger
                key={option.value}
                value={option.value}
                disabled={disabled}
                // The brand teal is applied inline so the active mode stays unmistakable regardless of primitive variant styling.
                style={isActive ? { backgroundColor: "#0f766e", color: "#ffffff" } : undefined}
                className="h-auto min-h-10 rounded-[0.8rem] px-3 py-2 text-left text-[11px] leading-5 font-semibold whitespace-normal text-[#4f6d71] sm:justify-center sm:text-center"
              >
                {option.label}
              </TabsTrigger>
            );
          })}
        </TabsList>
      </Tabs>
    </div>
  );
}
